import React, { useState } from "react";
import * as XLSX from "xlsx";

const fmt = (n) => "₪" + Math.round(n).toLocaleString();
const workerListCost = (list) =>
  list.reduce(
    (s, w) => s + (parseFloat(w.daily) || 0) * (parseFloat(w.days) || 0),
    0,
  );
const equipmentCost = (list) =>
  list.reduce((s, eq) => {
    if (eq.owned) return s;
    if (eq.costType === "fixed") return s + (parseFloat(eq.fixedCost) || 0);
    return s + (parseFloat(eq.daily) || 0) * (parseFloat(eq.days) || 0);
  }, 0);

const newWorker = () => ({ name: "", daily: "", days: "", type: "exp" });
const newEquipment = () => ({
  type: "truck",
  name: "",
  owned: false,
  costType: "daily",
  daily: "",
  days: "",
  fixedCost: "",
});

const inp =
  "w-full px-3 py-2.5 rounded-xl text-sm bg-gray-50 border border-gray-200 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100 transition-all placeholder-gray-300";

function SectionLabel({ icon, title, count }) {
  return (
    <div className="flex items-center gap-2.5 mt-8 mb-3">
      <div className="w-7 h-7 rounded-lg bg-slate-800 flex items-center justify-center text-white text-sm flex-shrink-0">
        {icon}
      </div>
      <span className="text-sm font-semibold text-slate-700 tracking-wide">
        {title}
      </span>
      {count !== undefined && (
        <span className="ml-auto text-xs text-slate-400 font-medium">
          {count} added
        </span>
      )}
    </div>
  );
}

function Field({ label, suffix, children }) {
  return (
    <div>
      <p className="text-xs font-medium text-slate-400 mb-1.5 tracking-wide uppercase">
        {label}
      </p>
      <div className="flex items-center gap-2">
        {children}
        {suffix && (
          <span className="text-xs text-slate-400 font-medium flex-shrink-0">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

function WorkerCard({ worker, index, projDays, onChange, onRemove }) {
  const cost = (parseFloat(worker.daily) || 0) * (parseFloat(worker.days) || 0);
  const daysLeft = (parseFloat(projDays) || 0) - (parseFloat(worker.days) || 0);
  const isExp = worker.type === "exp";

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mb-3 transition-all hover:border-slate-200">
      <div
        className={`h-1 w-full ${isExp ? "bg-slate-700" : "bg-amber-400"}`}
      />
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-1.5 p-1 bg-gray-50 rounded-xl border border-gray-100">
            <button
              onClick={() => onChange(index, "type", "exp")}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                isExp
                  ? "bg-slate-800 text-white shadow-sm"
                  : "text-slate-400 hover:text-slate-600"
              }`}
            >
              Experienced
            </button>
            <button
              onClick={() => onChange(index, "type", "nov")}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                !isExp
                  ? "bg-amber-400 text-white shadow-sm"
                  : "text-slate-400 hover:text-slate-600"
              }`}
            >
              Junior
            </button>
          </div>
          <button
            onClick={() => onRemove(index)}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-400 hover:bg-red-50 transition-all text-lg leading-none"
          >
            ×
          </button>
        </div>

        {/* Stack to single column on mobile, 3-col on sm+ */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Name">
            <input
              type="text"
              placeholder="e.g. Ahmad"
              value={worker.name}
              onChange={(e) => onChange(index, "name", e.target.value)}
              className={inp}
            />
          </Field>
          <Field label="Daily wage" suffix="₪">
            <input
              type="number"
              placeholder="0"
              value={worker.daily}
              onChange={(e) => onChange(index, "daily", e.target.value)}
              className={inp}
            />
          </Field>
          <Field label="Days">
            <input
              type="number"
              placeholder="0"
              value={worker.days}
              onChange={(e) => onChange(index, "days", e.target.value)}
              className={inp}
            />
          </Field>
        </div>

        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
          <span className="text-xs text-slate-400">
            Total:{" "}
            <span className="font-semibold text-slate-700">{fmt(cost)}</span>
          </span>
          {projDays > 0 && (
            <span
              className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                daysLeft < 0
                  ? "bg-red-50 text-red-500"
                  : "bg-emerald-50 text-emerald-600"
              }`}
            >
              {daysLeft >= 0
                ? `${daysLeft} days remaining`
                : `${Math.abs(daysLeft)} days over`}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function EquipmentCard({ eq, index, projDays, onChange, onRemove }) {
  const cost = eq.owned
    ? 0
    : eq.costType === "fixed"
      ? parseFloat(eq.fixedCost) || 0
      : (parseFloat(eq.daily) || 0) * (parseFloat(eq.days) || 0);
  const daysLeft = (parseFloat(projDays) || 0) - (parseFloat(eq.days) || 0);

  const typeOptions = [
    { value: "truck", label: "Truck" },
    { value: "bobcat", label: "Bobcat" },
    { value: "excavator", label: "Excavator" },
    { value: "crane", label: "Crane" },
    { value: "other", label: "Other" },
  ];

  return (
    <div
      className={`bg-white rounded-2xl border overflow-hidden mb-3 transition-all hover:border-slate-200 ${
        eq.owned ? "border-emerald-100" : "border-gray-100"
      }`}
    >
      <div
        className={`h-1 w-full ${eq.owned ? "bg-emerald-400" : "bg-violet-500"}`}
      />
      <div className="p-4">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <select
            value={eq.type}
            onChange={(e) => onChange(index, "type", e.target.value)}
            className="flex-1 min-w-0 px-3 py-2 rounded-xl text-sm bg-gray-50 border border-gray-200 outline-none focus:border-slate-400 text-slate-600 font-medium"
          >
            {typeOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => onChange(index, "owned", !eq.owned)}
            className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${
              eq.owned
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-gray-50 text-slate-400 border-gray-200 hover:border-slate-300 hover:text-slate-600"
            }`}
          >
            {eq.owned ? "✓ Owned" : "Mark as owned"}
          </button>
          <button
            onClick={() => onRemove(index)}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-400 hover:bg-red-50 transition-all text-lg leading-none flex-shrink-0"
          >
            ×
          </button>
        </div>

        <div className="mb-3">
          <Field label="Name / label">
            <input
              type="text"
              placeholder="e.g. Bobcat #2"
              value={eq.name}
              onChange={(e) => onChange(index, "name", e.target.value)}
              className={inp}
            />
          </Field>
        </div>

        {!eq.owned && (
          <>
            <div className="flex gap-1.5 p-1 bg-gray-50 rounded-xl border border-gray-100 mb-3">
              {["daily", "fixed"].map((t) => (
                <button
                  key={t}
                  onClick={() => onChange(index, "costType", t)}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    eq.costType === t
                      ? "bg-violet-600 text-white shadow-sm"
                      : "text-slate-400 hover:text-slate-600"
                  }`}
                >
                  {t === "daily" ? "Daily rate × days" : "Fixed rental"}
                </button>
              ))}
            </div>
            {eq.costType === "daily" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Daily rate" suffix="₪">
                  <input
                    type="number"
                    placeholder="0"
                    value={eq.daily}
                    onChange={(e) => onChange(index, "daily", e.target.value)}
                    className={inp}
                  />
                </Field>
                <Field label="Days used">
                  <input
                    type="number"
                    placeholder="0"
                    value={eq.days}
                    onChange={(e) => onChange(index, "days", e.target.value)}
                    className={inp}
                  />
                </Field>
              </div>
            ) : (
              <Field label="Total rental price" suffix="₪">
                <input
                  type="number"
                  placeholder="0"
                  value={eq.fixedCost}
                  onChange={(e) => onChange(index, "fixedCost", e.target.value)}
                  className={inp}
                />
              </Field>
            )}
          </>
        )}

        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
          {eq.owned ? (
            <span className="text-xs font-semibold text-emerald-600 px-2.5 py-1 bg-emerald-50 rounded-full">
              Owned — no cost
            </span>
          ) : (
            <span className="text-xs text-slate-400">
              Total:{" "}
              <span className="font-semibold text-slate-700">{fmt(cost)}</span>
            </span>
          )}
          {!eq.owned && eq.costType === "daily" && projDays > 0 && (
            <span
              className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                daysLeft < 0
                  ? "bg-red-50 text-red-500"
                  : "bg-emerald-50 text-emerald-600"
              }`}
            >
              {daysLeft >= 0
                ? `${daysLeft} days remaining`
                : `${Math.abs(daysLeft)} days over`}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function AddBtn({ onClick, label }) {
  return (
    <button
      onClick={onClick}
      className="w-full py-3 rounded-2xl border border-dashed border-gray-200 text-sm text-slate-400 font-medium hover:border-slate-300 hover:text-slate-600 hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
    >
      <span className="text-lg leading-none">+</span> {label}
    </button>
  );
}

export default function BossCalcSmallProject() {
  const [contractPrice, setContractPrice] = useState("");
  const [projDays, setProjDays] = useState("");
  const [workers, setWorkers] = useState([newWorker()]);
  const [equipment, setEquipment] = useState([newEquipment()]);
  const [transport, setTransport] = useState({ daily: "", days: "" });
  const [expenses, setExpenses] = useState([
    { label: "Materials", amount: "" },
    { label: "Equipment", amount: "" },
  ]);

  const updateWorker = (i, f, v) =>
    setWorkers((p) => p.map((w, idx) => (idx === i ? { ...w, [f]: v } : w)));
  const removeWorker = (i) =>
    setWorkers((p) => p.filter((_, idx) => idx !== i));
  const updateEquipment = (i, f, v) =>
    setEquipment((p) =>
      p.map((eq, idx) => (idx === i ? { ...eq, [f]: v } : eq)),
    );
  const removeEquipment = (i) =>
    setEquipment((p) => p.filter((_, idx) => idx !== i));
  const updateExpense = (i, f, v) =>
    setExpenses((p) => p.map((e, idx) => (idx === i ? { ...e, [f]: v } : e)));
  const removeExpense = (i) =>
    setExpenses((p) => p.filter((_, idx) => idx !== i));

  const price = parseFloat(contractPrice) || 0;
  const projDaysNum = parseFloat(projDays) || 0;
  const totalWages = workerListCost(workers);
  const totalEquip = equipmentCost(equipment);
  const totalTransport =
    (parseFloat(transport.daily) || 0) * (parseFloat(transport.days) || 0);
  const totalExtras = expenses.reduce(
    (s, e) => s + (parseFloat(e.amount) || 0),
    0,
  );
  const totalCost = totalWages + totalEquip + totalTransport + totalExtras;
  const profit = price - totalCost;
  const margin = price > 0 ? Math.round((profit / price) * 100) : 0;
  const profitPerDay = projDaysNum > 0 ? profit / projDaysNum : null;

  const exportExcel = () => {
    const rows = [];
    rows.push({
      Section: "PROJECT",
      Item: "Contract price",
      Amount: price,
      Note: "",
    });
    rows.push({
      Section: "PROJECT",
      Item: "Expected working days",
      Amount: projDaysNum,
      Note: "days",
    });
    if (projDaysNum > 0) {
      rows.push({
        Section: "PROJECT",
        Item: "Profit per day",
        Amount: Math.round(profitPerDay),
        Note: "₪/day",
      });
    }
    rows.push({});
    workers.forEach((w) => {
      const c = (parseFloat(w.daily) || 0) * (parseFloat(w.days) || 0);
      rows.push({
        Section: "WORKER",
        Item: w.name || "Worker",
        Amount: c,
        Note: `${w.type === "exp" ? "Experienced" : "Junior"} · ${parseFloat(w.days) || 0} days × ₪${parseFloat(w.daily) || 0}/day`,
      });
    });
    rows.push({});
    equipment.forEach((eq) => {
      const c = eq.owned
        ? 0
        : eq.costType === "fixed"
          ? parseFloat(eq.fixedCost) || 0
          : (parseFloat(eq.daily) || 0) * (parseFloat(eq.days) || 0);
      rows.push({
        Section: "EQUIPMENT",
        Item: eq.name || eq.type,
        Amount: eq.owned ? 0 : c,
        Note: eq.owned
          ? "Owned — no cost"
          : eq.costType === "fixed"
            ? "Fixed rental"
            : `${parseFloat(eq.days) || 0} days × ₪${parseFloat(eq.daily) || 0}/day`,
      });
    });
    rows.push({});
    rows.push({
      Section: "TRANSPORTATION",
      Item: "Daily transport",
      Amount: totalTransport,
      Note: `${parseFloat(transport.days) || 0} days × ₪${parseFloat(transport.daily) || 0}/day`,
    });
    rows.push({});
    expenses.forEach((e) => {
      if (e.label || e.amount)
        rows.push({
          Section: "EXPENSE",
          Item: e.label || "Expense",
          Amount: parseFloat(e.amount) || 0,
          Note: "",
        });
    });
    rows.push({});
    rows.push({ Section: "── SUMMARY ──", Item: "", Amount: "", Note: "" });
    rows.push({
      Section: "Total wages",
      Item: "",
      Amount: totalWages,
      Note: "",
    });
    rows.push({
      Section: "Total equipment",
      Item: "",
      Amount: totalEquip,
      Note: "",
    });
    rows.push({
      Section: "Transportation",
      Item: "",
      Amount: totalTransport,
      Note: "",
    });
    rows.push({
      Section: "Total expenses",
      Item: "",
      Amount: totalExtras,
      Note: "",
    });
    rows.push({ Section: "Total cost", Item: "", Amount: totalCost, Note: "" });
    rows.push({
      Section: "Net profit",
      Item: "",
      Amount: profit,
      Note: `${margin}% margin`,
    });
    if (projDaysNum > 0)
      rows.push({
        Section: "Profit per day",
        Item: "",
        Amount: Math.round(profitPerDay),
        Note: `over ${projDaysNum} days`,
      });

    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 20 }, { wch: 30 }, { wch: 15 }, { wch: 45 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Calculation");
    XLSX.writeFile(wb, `calc_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportPDF = () => {
    const profitColor = profit >= 0 ? "#1D9E75" : "#E24B4A";
    const workerRows = workers
      .map((w) => {
        const c = (parseFloat(w.daily) || 0) * (parseFloat(w.days) || 0);
        if (!c && !w.name) return "";
        return `<tr><td>${w.name || "Worker"}</td><td><span class="badge ${w.type === "exp" ? "badge-exp" : "badge-nov"}">${w.type === "exp" ? "Experienced" : "Junior"}</span></td><td>${parseFloat(w.days) || 0} days</td><td>₪${parseFloat(w.daily) || 0}/day</td><td class="amount">-₪${Math.round(c).toLocaleString()}</td></tr>`;
      })
      .join("");
    const equipRows = equipment
      .map((eq) => {
        const c = eq.owned
          ? 0
          : eq.costType === "fixed"
            ? parseFloat(eq.fixedCost) || 0
            : (parseFloat(eq.daily) || 0) * (parseFloat(eq.days) || 0);
        const label =
          eq.name || eq.type.charAt(0).toUpperCase() + eq.type.slice(1);
        if (!c && !eq.name) return "";
        return `<tr><td>${label}</td><td><span class="badge ${eq.owned ? "badge-owned" : "badge-equip"}">${eq.owned ? "Owned" : eq.costType === "fixed" ? "Fixed" : "Daily"}</span></td><td>${eq.owned ? "—" : eq.costType === "daily" ? `${parseFloat(eq.days) || 0} days` : "—"}</td><td>${eq.owned ? "No cost" : eq.costType === "daily" ? `₪${parseFloat(eq.daily) || 0}/day` : "Fixed price"}</td><td class="amount ${eq.owned ? "owned" : ""}">${eq.owned ? "₪0" : `-₪${Math.round(c).toLocaleString()}`}</td></tr>`;
      })
      .join("");
    const transportRow =
      totalTransport > 0
        ? `<tr><td>Transportation</td><td><span class="badge badge-transport">Daily</span></td><td>${parseFloat(transport.days) || 0} days</td><td>₪${parseFloat(transport.daily) || 0}/day</td><td class="amount">-₪${Math.round(totalTransport).toLocaleString()}</td></tr>`
        : "";
    const expenseRows = expenses
      .filter((e) => e.label || e.amount)
      .map(
        (e) =>
          `<tr><td colspan="4">${e.label || "Expense"}</td><td class="amount">-₪${Math.round(parseFloat(e.amount) || 0).toLocaleString()}</td></tr>`,
      )
      .join("");
    const profitPerDayRow =
      projDaysNum > 0
        ? `<div class="summary-row per-day"><span class="sk">Per day · ${projDaysNum} days</span><span class="sv">₪${Math.round(profitPerDay).toLocaleString()}<span class="per-day-label">/day</span></span></div>`
        : "";

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Project Calculation</title><style>
    @media print { @page { size: A4; margin: 15mm; } body { margin: 0; } }
    * { box-sizing: border-box; } body { font-family: Arial, sans-serif; color: #1a1a1a; background: white; padding: 32px; }
    .header { background: #1e293b; color: white; padding: 24px 28px; border-radius: 12px; margin-bottom: 24px; }
    .header h1 { margin: 0 0 4px; font-size: 22px; font-weight: 700; letter-spacing: -0.5px; } .header p { margin: 0; font-size: 13px; opacity: 0.6; }
    .metrics { display: grid; grid-template-columns: repeat(6, 1fr); gap: 8px; margin-bottom: 24px; }
    .metric { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px; }
    .metric .label { font-size: 10px; color: #64748b; margin-bottom: 5px; text-transform: uppercase; letter-spacing: 0.5px; }
    .metric .val { font-size: 15px; font-weight: 700; color: #1e293b; } .metric .sub { font-size: 10px; color: #94a3b8; margin-top: 3px; }
    .metric.profit { background: ${profit >= 0 ? "#f0fdf4" : "#fff1f2"}; border-color: ${profit >= 0 ? "#86efac" : "#fca5a5"}; }
    .metric.profit .val { color: ${profitColor}; } .metric.profit .sub { color: ${profit >= 0 ? "#4ade80" : "#f87171"}; }
    .section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #64748b; margin: 22px 0 8px; display: flex; align-items: center; gap: 8px; }
    .section-title::after { content: ""; flex: 1; height: 1px; background: #e2e8f0; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    th { background: #f1f5f9; padding: 9px 12px; text-align: left; font-weight: 600; color: #475569; border-bottom: 1px solid #e2e8f0; }
    td { padding: 9px 12px; border-bottom: 1px solid #f1f5f9; color: #334155; vertical-align: middle; }
    tr:last-child td { border-bottom: none; } td.amount { text-align: right; font-weight: 700; color: #E24B4A; } td.amount.owned { color: #1D9E75; }
    .badge { display: inline-block; padding: 2px 9px; border-radius: 20px; font-size: 10px; font-weight: 700; }
    .badge-exp { background: #dbeafe; color: #1e40af; } .badge-nov { background: #fef3c7; color: #92400e; }
    .badge-equip { background: #ede9fe; color: #5b21b6; } .badge-owned { background: #dcfce7; color: #166534; } .badge-transport { background: #e0f2fe; color: #075985; }
    .summary-box { margin-top: 24px; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; }
    .summary-row { display: flex; justify-content: space-between; padding: 11px 18px; border-bottom: 1px solid #f1f5f9; font-size: 13px; background: #f8fafc; }
    .summary-row.profit-row { border-bottom: none; background: ${profit >= 0 ? "#f0fdf4" : "#fff1f2"}; padding: 14px 18px; }
    .summary-row.per-day { border-bottom: none; background: ${profit >= 0 ? "#dcfce7" : "#fee2e2"}; padding: 12px 18px; }
    .summary-row .sk { color: #64748b; } .summary-row .sv { font-weight: 700; color: #1e293b; }
    .summary-row.profit-row .sk, .summary-row.profit-row .sv { color: ${profitColor}; font-size: 16px; }
    .summary-row.per-day .sk { color: ${profit >= 0 ? "#16a34a" : "#dc2626"}; font-size: 12px; }
    .summary-row.per-day .sv { color: ${profit >= 0 ? "#15803d" : "#b91c1c"}; font-size: 14px; }
    .per-day-label { font-size: 11px; font-weight: 400; margin-left: 2px; opacity: 0.75; }
    .footer { margin-top: 28px; text-align: center; font-size: 11px; color: #94a3b8; border-top: 1px solid #f1f5f9; padding-top: 16px; }
    </style></head><body>
    <div class="header"><h1>Project calculation</h1><p>Generated ${new Date().toLocaleDateString()} · ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · ${projDaysNum > 0 ? `${projDaysNum} expected working days` : "No duration set"}</p></div>
    <div class="metrics">
      <div class="metric"><div class="label">Contract price</div><div class="val">₪${Math.round(price).toLocaleString()}</div></div>
      <div class="metric"><div class="label">Worker wages</div><div class="val">₪${Math.round(totalWages).toLocaleString()}</div></div>
      <div class="metric"><div class="label">Equipment</div><div class="val">₪${Math.round(totalEquip).toLocaleString()}</div></div>
      <div class="metric"><div class="label">Transportation</div><div class="val">₪${Math.round(totalTransport).toLocaleString()}</div></div>
      <div class="metric"><div class="label">Other expenses</div><div class="val">₪${Math.round(totalExtras).toLocaleString()}</div></div>
      <div class="metric profit"><div class="label">Net profit</div><div class="val">₪${Math.round(profit).toLocaleString()}</div>${projDaysNum > 0 ? `<div class="sub">₪${Math.round(profitPerDay).toLocaleString()}/day</div>` : ""}</div>
    </div>
    ${workerRows ? `<div class="section-title">Workers</div><table><thead><tr><th>Name</th><th>Type</th><th>Days</th><th>Rate</th><th style="text-align:right">Cost</th></tr></thead><tbody>${workerRows}</tbody></table>` : ""}
    ${equipRows ? `<div class="section-title">Equipment & machinery</div><table><thead><tr><th>Name</th><th>Type</th><th>Duration</th><th>Rate</th><th style="text-align:right">Cost</th></tr></thead><tbody>${equipRows}</tbody></table>` : ""}
    ${transportRow ? `<div class="section-title">Transportation</div><table><thead><tr><th>Item</th><th>Type</th><th>Duration</th><th>Rate</th><th style="text-align:right">Cost</th></tr></thead><tbody>${transportRow}</tbody></table>` : ""}
    ${expenseRows ? `<div class="section-title">Extra expenses</div><table><thead><tr><th colspan="4">Description</th><th style="text-align:right">Amount</th></tr></thead><tbody>${expenseRows}</tbody></table>` : ""}
    <div class="summary-box">
      <div class="summary-row"><span class="sk">Total wages</span><span class="sv">-₪${Math.round(totalWages).toLocaleString()}</span></div>
      <div class="summary-row"><span class="sk">Total equipment</span><span class="sv">-₪${Math.round(totalEquip).toLocaleString()}</span></div>
      <div class="summary-row"><span class="sk">Transportation</span><span class="sv">-₪${Math.round(totalTransport).toLocaleString()}</span></div>
      <div class="summary-row"><span class="sk">Total expenses</span><span class="sv">-₪${Math.round(totalExtras).toLocaleString()}</span></div>
      <div class="summary-row"><span class="sk">Total cost</span><span class="sv">-₪${Math.round(totalCost).toLocaleString()}</span></div>
      <div class="summary-row profit-row"><span class="sk">Net profit · ${margin}% margin</span><span class="sv">₪${Math.round(profit).toLocaleString()}</span></div>
      ${profitPerDayRow}
    </div>
    <div class="footer">NeholEsek · Project Calculator</div>
    <script>window.onload = function() { window.print(); setTimeout(function() { window.close(); }, 500); };</script>
    </body></html>`;

    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
  };

  const metrics = [
    { label: "Contract price", val: fmt(price), sub: null },
    {
      label: "Total wages",
      val: fmt(totalWages),
      sub: `${workers.length} worker${workers.length !== 1 ? "s" : ""}`,
    },
    {
      label: "Equipment",
      val: fmt(totalEquip),
      sub: `${equipment.filter((e) => !e.owned).length} rented`,
    },
    {
      label: "Transportation",
      val: fmt(totalTransport),
      sub: `${transport.days || 0} days`,
    },
    { label: "Other expenses", val: fmt(totalExtras), sub: null },
    {
      label: "Net profit",
      val: fmt(profit),
      sub:
        projDaysNum > 0
          ? `${fmt(Math.round(profitPerDay))}/day · ${margin}% margin`
          : `${margin}% margin`,
      accent: true,
      positive: profit >= 0,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Metrics strip — 2 cols on mobile, 3 on sm, 6 on md */}
      <div className="bg-slate-800 px-4 pb-3">
        <div className="max-w-2xl mx-auto grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 pt-3">
          {metrics.map((m) => (
            <div
              key={m.label}
              className={`rounded-xl p-2.5 ${m.accent ? (m.positive ? "bg-emerald-500" : "bg-red-500") : "bg-slate-700"}`}
            >
              <p
                className={`text-xs mb-1 font-medium ${m.accent ? "text-white opacity-80" : "text-slate-400"}`}
              >
                {m.label}
              </p>
              <p className="text-xs font-bold text-white">{m.val}</p>
              {m.sub && (
                <p
                  className={`text-xs mt-0.5 ${m.accent ? "text-white opacity-70" : "text-slate-500"}`}
                >
                  {m.sub}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pb-12">
        {/* Project details */}
        <SectionLabel icon="📋" title="Project details" />
        <div className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Contract price" suffix="₪">
              <input
                type="number"
                placeholder="0"
                value={contractPrice}
                onChange={(e) => setContractPrice(e.target.value)}
                className={inp}
              />
            </Field>
            <Field label="Expected working days" suffix="days">
              <input
                type="number"
                placeholder="0"
                value={projDays}
                onChange={(e) => setProjDays(e.target.value)}
                className={inp}
              />
            </Field>
          </div>
        </div>

        {/* Workers */}
        <SectionLabel icon="👷" title="Workers" count={workers.length} />
        {workers.map((w, i) => (
          <WorkerCard
            key={i}
            worker={w}
            index={i}
            projDays={projDaysNum}
            onChange={updateWorker}
            onRemove={removeWorker}
          />
        ))}
        <AddBtn
          onClick={() => setWorkers((p) => [...p, newWorker()])}
          label="Add worker"
        />

        {/* Equipment */}
        <SectionLabel
          icon="🚜"
          title="Equipment & machinery"
          count={equipment.length}
        />
        {equipment.map((eq, i) => (
          <EquipmentCard
            key={i}
            eq={eq}
            index={i}
            projDays={projDaysNum}
            onChange={updateEquipment}
            onRemove={removeEquipment}
          />
        ))}
        <AddBtn
          onClick={() => setEquipment((p) => [...p, newEquipment()])}
          label="Add equipment"
        />

        {/* Transportation */}
        <SectionLabel icon="🚗" title="Transportation" />
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mb-1">
          <div className="h-1 w-full bg-sky-400" />
          <div className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Daily transport cost" suffix="₪">
                <input
                  type="number"
                  placeholder="0"
                  value={transport.daily}
                  onChange={(e) =>
                    setTransport((p) => ({ ...p, daily: e.target.value }))
                  }
                  className={inp}
                />
              </Field>
              <Field label="Number of days">
                <input
                  type="number"
                  placeholder="0"
                  value={transport.days}
                  onChange={(e) =>
                    setTransport((p) => ({ ...p, days: e.target.value }))
                  }
                  className={inp}
                />
              </Field>
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
              <span className="text-xs text-slate-400">
                Total:{" "}
                <span className="font-semibold text-slate-700">
                  {fmt(totalTransport)}
                </span>
              </span>
              {projDaysNum > 0 && transport.days && (
                <span
                  className={`text-xs font-medium px-2.5 py-1 rounded-full ${(parseFloat(transport.days) || 0) > projDaysNum ? "bg-red-50 text-red-500" : "bg-emerald-50 text-emerald-600"}`}
                >
                  {projDaysNum - (parseFloat(transport.days) || 0) >= 0
                    ? `${projDaysNum - (parseFloat(transport.days) || 0)} days remaining`
                    : `${Math.abs(projDaysNum - (parseFloat(transport.days) || 0))} days over`}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Extra expenses */}
        <SectionLabel
          icon="🧾"
          title="Extra expenses"
          count={expenses.length}
        />
        <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-2.5">
          {expenses.map((e, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input
                type="text"
                placeholder="Description"
                value={e.label}
                onChange={(ev) => updateExpense(i, "label", ev.target.value)}
                className="flex-1 min-w-0 px-3 py-2.5 rounded-xl text-sm bg-gray-50 border border-gray-200 outline-none focus:border-slate-400 placeholder-gray-300"
              />
              <input
                type="number"
                placeholder="0"
                value={e.amount}
                onChange={(ev) => updateExpense(i, "amount", ev.target.value)}
                className="w-24 px-3 py-2.5 rounded-xl text-sm bg-gray-50 border border-gray-200 outline-none focus:border-slate-400 placeholder-gray-300"
              />
              <span className="text-xs text-slate-400 font-medium">₪</span>
              <button
                onClick={() => removeExpense(i)}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-400 hover:bg-red-50 transition-all text-lg leading-none flex-shrink-0"
              >
                ×
              </button>
            </div>
          ))}
          <button
            onClick={() =>
              setExpenses((p) => [...p, { label: "", amount: "" }])
            }
            className="w-full py-2.5 rounded-xl border border-dashed border-gray-200 text-sm text-slate-400 font-medium hover:border-slate-300 hover:bg-gray-50 transition-all mt-1"
          >
            + Add expense
          </button>
        </div>

        {/* Breakdown */}
        <SectionLabel icon="📊" title="Cost breakdown" />
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="p-4 sm:p-5 space-y-1">
            <div className="flex justify-between py-1.5 text-sm">
              <span className="text-slate-500">Contract price</span>
              <span className="font-semibold text-slate-800">{fmt(price)}</span>
            </div>
            {workers.map((w, i) => {
              const c = (parseFloat(w.daily) || 0) * (parseFloat(w.days) || 0);
              if (!c && !w.name) return null;
              return (
                <div
                  key={`w-${i}`}
                  className="flex justify-between py-1.5 text-sm gap-2"
                >
                  <span className="text-slate-500 flex items-center gap-2 flex-wrap min-w-0">
                    <span className="truncate">{w.name || "Worker"}</span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${w.type === "exp" ? "bg-slate-100 text-slate-600" : "bg-amber-50 text-amber-700"}`}
                    >
                      {w.type === "exp" ? "exp" : "junior"}
                    </span>
                    <span className="text-xs text-slate-400 flex-shrink-0">
                      {parseFloat(w.days) || 0}d × ₪{parseFloat(w.daily) || 0}
                    </span>
                  </span>
                  <span className="font-medium text-red-500 flex-shrink-0">
                    -{fmt(c)}
                  </span>
                </div>
              );
            })}
            {equipment.map((eq, i) => {
              const c = eq.owned
                ? 0
                : eq.costType === "fixed"
                  ? parseFloat(eq.fixedCost) || 0
                  : (parseFloat(eq.daily) || 0) * (parseFloat(eq.days) || 0);
              const label =
                eq.name || eq.type.charAt(0).toUpperCase() + eq.type.slice(1);
              if (!c && !eq.name) return null;
              return (
                <div
                  key={`eq-${i}`}
                  className="flex justify-between py-1.5 text-sm gap-2"
                >
                  <span className="text-slate-500 flex items-center gap-2 flex-wrap min-w-0">
                    <span className="truncate">{label}</span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${eq.owned ? "bg-emerald-50 text-emerald-700" : "bg-violet-50 text-violet-700"}`}
                    >
                      {eq.owned
                        ? "owned"
                        : eq.costType === "fixed"
                          ? "fixed"
                          : `${parseFloat(eq.days) || 0}d`}
                    </span>
                  </span>
                  <span
                    className={`font-medium flex-shrink-0 ${eq.owned ? "text-emerald-600" : "text-red-500"}`}
                  >
                    {eq.owned ? "₪0" : `-${fmt(c)}`}
                  </span>
                </div>
              );
            })}
            {totalTransport > 0 && (
              <div className="flex justify-between py-1.5 text-sm gap-2">
                <span className="text-slate-500 flex items-center gap-2 flex-wrap min-w-0">
                  Transportation
                  <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-sky-50 text-sky-700 flex-shrink-0">
                    {parseFloat(transport.days) || 0}d × ₪
                    {parseFloat(transport.daily) || 0}
                  </span>
                </span>
                <span className="font-medium text-red-500 flex-shrink-0">
                  -{fmt(totalTransport)}
                </span>
              </div>
            )}
            {expenses.map((e, i) =>
              e.label || e.amount ? (
                <div
                  key={`ex-${i}`}
                  className="flex justify-between py-1.5 text-sm gap-2"
                >
                  <span className="text-slate-500 truncate">
                    {e.label || "Expense"}
                  </span>
                  <span className="font-medium text-red-500 flex-shrink-0">
                    -{fmt(parseFloat(e.amount) || 0)}
                  </span>
                </div>
              ) : null,
            )}
          </div>

          <div
            className={`px-4 sm:px-5 py-4 flex items-center justify-between border-t gap-3 ${profit >= 0 ? "border-emerald-100 bg-emerald-50" : "border-red-100 bg-red-50"}`}
          >
            <div>
              <p
                className={`text-xs font-semibold uppercase tracking-wider mb-0.5 ${profit >= 0 ? "text-emerald-600" : "text-red-500"}`}
              >
                Net profit
              </p>
              <p
                className={`text-xs ${profit >= 0 ? "text-emerald-500" : "text-red-400"}`}
              >
                {margin}% margin
              </p>
            </div>
            <div className="text-right">
              <p
                className={`text-xl sm:text-2xl font-bold ${profit >= 0 ? "text-emerald-600" : "text-red-500"}`}
              >
                {fmt(profit)}
              </p>
              {projDaysNum > 0 && (
                <p
                  className={`text-xs font-semibold mt-0.5 ${profit >= 0 ? "text-emerald-500" : "text-red-400"}`}
                >
                  {fmt(Math.round(profitPerDay))}
                  <span className="font-normal opacity-75"> / day</span>
                  <span className="font-normal opacity-60 ml-1">
                    over {projDaysNum} days
                  </span>
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Export buttons */}
        <div className="flex flex-wrap gap-3 mt-6 mb-8">
          <button
            onClick={exportExcel}
            className="flex items-center gap-2 px-5 py-3 rounded-xl bg-emerald-50 text-emerald-800 border border-emerald-200 text-sm font-semibold hover:bg-emerald-100 transition-all"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
            Export Excel
          </button>
          <button
            onClick={exportPDF}
            className="flex items-center gap-2 px-5 py-3 rounded-xl bg-orange-50 text-orange-800 border border-orange-200 text-sm font-semibold hover:bg-orange-100 transition-all"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
            Export PDF
          </button>
        </div>
      </div>
    </div>
  );
}
